import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import AsyncSelect from 'react-select/async';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config/api';

const emptyItem = () => ({
  item_id: '',
  item_label: '',
  quantity: '1',
  rate: '',
  units: '',
  description: '',
  location: '',
  custcol_ice_ld_discount: '',
});

const CreateOrder = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [customer, setCustomer] = useState(null); // { value, label, code }
  const [locations, setLocations] = useState({}); // { id: name }
  const [shippingAddresses, setShippingAddresses] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const [header, setHeader] = useState({
    tranDate: new Date().toISOString().slice(0, 10),
    location_id: '',
    shipaddresslist: '',
    otherrefnum: '',
    memo: '',
    custbody_ar_req_inv_mac5: '',
    custbodyar_so_memo2: '',
    custbody_ar_all_memo: '',
    custbody_ar_estimate_contrat1: '',
  });

  const [items, setItems] = useState([emptyItem()]);

  // Load location options once
  useEffect(() => {
    axios.get(`${API_URL}/locations`)
      .then(r => { if (r.data?.success) setLocations(r.data.locations || {}); })
      .catch(() => {});
  }, []);

  // When customer changes, load its shipping addresses
  useEffect(() => {
    if (!customer?.value) { setShippingAddresses([]); return; }
    axios.get(`${API_URL}/customer/${customer.value}/shipping`)
      .then(r => { if (r.data?.success) setShippingAddresses(r.data.shippingAddresses || []); })
      .catch(() => setShippingAddresses([]));
    setHeader(h => ({ ...h, shipaddresslist: '' }));
  }, [customer]);

  const loadCustomers = useCallback((input, cb) => {
    if (!input || input.trim().length < 2) { cb([]); return; }
    axios.get(`${API_URL}/customers/search`, { params: { q: input } })
      .then(r => {
        const opts = (r.data?.customers || []).map(c => ({
          value: c.id, code: c.code, label: `${c.name}${c.code ? ` (${c.code})` : ''}`,
        }));
        cb(opts);
      })
      .catch(() => cb([]));
  }, []);

  const loadItems = useCallback((input, cb) => {
    if (!input || input.trim().length < 2) { cb([]); return; }
    axios.get(`${API_URL}/items/search`, { params: { q: input } })
      .then(r => {
        const opts = (r.data?.items || []).map(it => ({ value: it.id, label: it.name }));
        cb(opts);
      })
      .catch(() => cb([]));
  }, []);

  const setItemField = (idx, field, value) => {
    setItems(prev => prev.map((it, i) => (i === idx ? { ...it, [field]: value } : it)));
  };

  const addItem = () => setItems(prev => [...prev, emptyItem()]);
  const removeItem = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));

  const handleHeader = (field) => (e) => setHeader(h => ({ ...h, [field]: e.target.value }));

  const total = items.reduce((sum, it) => {
    const q = parseFloat(it.quantity) || 0;
    const r = parseFloat(it.rate) || 0;
    const d = parseFloat(it.custcol_ice_ld_discount) || 0;
    return sum + (q * r - d);
  }, 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!customer?.value) { toast.error('กรุณาเลือกลูกค้า'); return; }
    const validItems = items.filter(it => it.item_id && Number(it.quantity) > 0);
    if (validItems.length === 0) { toast.error('กรุณาเพิ่มรายการสินค้าอย่างน้อย 1 รายการ'); return; }

    const payload = {
      entity_id: customer.value,
      staffCode: currentUser?.staff_code || currentUser?.employee_id || null,
      ...header,
      items: validItems.map(it => ({
        item_id: it.item_id,
        quantity: it.quantity,
        rate: it.rate,
        units: it.units,
        description: it.description,
        location: it.location || header.location_id,
        custcol_ice_ld_discount: it.custcol_ice_ld_discount,
      })),
    };

    try {
      setSubmitting(true);
      const res = await axios.post(`${API_URL}/order/create`, payload);
      if (res.data?.success) {
        toast.success(`สร้าง Sales Order สำเร็จ (ID: ${res.data.id})`);
        if (res.data.id) navigate(`/order/${res.data.id}`);
      } else {
        toast.error(res.data?.message || 'ไม่สามารถสร้าง Sales Order ได้');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'เกิดข้อผิดพลาดในการสร้าง Sales Order');
    } finally {
      setSubmitting(false);
    }
  };

  const locationOptions = Object.entries(locations);

  return (
    <div className="container-fluid py-4" style={{ maxWidth: 1100 }}>
      <div className="d-flex align-items-center mb-4">
        <i className="bi bi-file-earmark-plus-fill text-primary me-2" style={{ fontSize: '1.6rem' }}></i>
        <h3 className="mb-0 fw-bold">สร้าง Sale Order</h3>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Header card */}
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-header bg-white fw-semibold">ข้อมูลหลัก</div>
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">ลูกค้า (Customer) <span className="text-danger">*</span></label>
                <AsyncSelect
                  cacheOptions
                  loadOptions={loadCustomers}
                  value={customer}
                  onChange={setCustomer}
                  placeholder="พิมพ์ชื่อหรือรหัสลูกค้า (อย่างน้อย 2 ตัวอักษร)..."
                  noOptionsMessage={() => 'พิมพ์เพื่อค้นหา'}
                  isClearable
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">วันที่ (Date)</label>
                <input type="date" className="form-control" value={header.tranDate} onChange={handleHeader('tranDate')} />
              </div>
              <div className="col-md-3">
                <label className="form-label">สถานที่ (Location)</label>
                <select className="form-select" value={header.location_id} onChange={handleHeader('location_id')}>
                  <option value="">เลือกสถานที่</option>
                  {locationOptions.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
                </select>
              </div>

              <div className="col-md-6">
                <label className="form-label">ที่อยู่จัดส่ง (Shipping Address)</label>
                <select
                  className="form-select"
                  value={header.shipaddresslist}
                  onChange={handleHeader('shipaddresslist')}
                  disabled={!customer}
                >
                  <option value="">{customer ? 'เลือกที่อยู่จัดส่ง' : 'เลือกลูกค้าก่อน'}</option>
                  {shippingAddresses.map(a => (
                    <option key={a.address_internal_id} value={a.address_internal_id}>
                      {a.shipping_address}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label">เลขที่อ้างอิง (PO / Other Ref)</label>
                <input type="text" className="form-control" value={header.otherrefnum} onChange={handleHeader('otherrefnum')} />
              </div>

              <div className="col-md-6">
                <label className="form-label">Memo</label>
                <input type="text" className="form-control" value={header.memo} onChange={handleHeader('memo')} />
              </div>
              <div className="col-md-6">
                <label className="form-label">custbody_ar_req_inv_mac5</label>
                <input type="text" className="form-control" value={header.custbody_ar_req_inv_mac5} onChange={handleHeader('custbody_ar_req_inv_mac5')} />
              </div>
              <div className="col-md-6">
                <label className="form-label">หมายเหตุ 2 (custbodyar_so_memo2)</label>
                <input type="text" className="form-control" value={header.custbodyar_so_memo2} onChange={handleHeader('custbodyar_so_memo2')} />
              </div>
              <div className="col-md-6">
                <label className="form-label">หมายเหตุรวม (custbody_ar_all_memo)</label>
                <input type="text" className="form-control" value={header.custbody_ar_all_memo} onChange={handleHeader('custbody_ar_all_memo')} />
              </div>
              <div className="col-md-6">
                <label className="form-label">custbody_ar_estimate_contrat1</label>
                <input type="text" className="form-control" value={header.custbody_ar_estimate_contrat1} onChange={handleHeader('custbody_ar_estimate_contrat1')} />
              </div>
            </div>
          </div>
        </div>

        {/* Items card */}
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-header bg-white d-flex justify-content-between align-items-center">
            <span className="fw-semibold">รายการสินค้า ({items.length})</span>
            <button type="button" className="btn btn-sm btn-outline-primary" onClick={addItem}>
              <i className="bi bi-plus-lg me-1"></i> เพิ่มรายการ
            </button>
          </div>
          <div className="card-body">
            {items.map((it, idx) => (
              <div key={idx} className="row g-2 align-items-end mb-3 pb-3 border-bottom">
                <div className="col-md-4">
                  <label className="form-label small">สินค้า (Item) <span className="text-danger">*</span></label>
                  <AsyncSelect
                    cacheOptions
                    loadOptions={loadItems}
                    value={it.item_id ? { value: it.item_id, label: it.item_label } : null}
                    onChange={(opt) => {
                      setItemField(idx, 'item_id', opt?.value || '');
                      setItemField(idx, 'item_label', opt?.label || '');
                      if (opt && !it.description) setItemField(idx, 'description', opt.label);
                    }}
                    placeholder="ค้นหาสินค้า..."
                    noOptionsMessage={() => 'พิมพ์เพื่อค้นหา'}
                    isClearable
                  />
                </div>
                <div className="col-md-1">
                  <label className="form-label small">จำนวน</label>
                  <input type="number" min="0" step="any" className="form-control" value={it.quantity}
                    onChange={e => setItemField(idx, 'quantity', e.target.value)} />
                </div>
                <div className="col-md-2">
                  <label className="form-label small">ราคา/หน่วย</label>
                  <input type="number" min="0" step="any" className="form-control" value={it.rate}
                    onChange={e => setItemField(idx, 'rate', e.target.value)} />
                </div>
                <div className="col-md-2">
                  <label className="form-label small">ส่วนลด (บาท)</label>
                  <input type="number" min="0" step="any" className="form-control" value={it.custcol_ice_ld_discount}
                    onChange={e => setItemField(idx, 'custcol_ice_ld_discount', e.target.value)} />
                </div>
                <div className="col-md-2">
                  <label className="form-label small">สถานที่</label>
                  <select className="form-select" value={it.location}
                    onChange={e => setItemField(idx, 'location', e.target.value)}>
                    <option value="">ตามหลัก</option>
                    {locationOptions.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
                  </select>
                </div>
                <div className="col-md-1 text-end">
                  <button type="button" className="btn btn-sm btn-outline-danger" title="ลบ"
                    onClick={() => removeItem(idx)} disabled={items.length === 1}>
                    <i className="bi bi-trash"></i>
                  </button>
                </div>
                <div className="col-12">
                  <input type="text" className="form-control form-control-sm" placeholder="รายละเอียด (description)"
                    value={it.description} onChange={e => setItemField(idx, 'description', e.target.value)} />
                </div>
              </div>
            ))}
            <div className="text-end fw-bold">
              ยอดรวมโดยประมาณ: {total.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} บาท
            </div>
          </div>
        </div>

        <div className="d-flex justify-content-end gap-2">
          <button type="button" className="btn btn-light" onClick={() => navigate(-1)} disabled={submitting}>
            ยกเลิก
          </button>
          <button type="submit" className="btn btn-primary px-4" disabled={submitting}>
            {submitting ? (
              <><span className="spinner-border spinner-border-sm me-2" /> กำลังสร้าง...</>
            ) : (
              <><i className="bi bi-check-lg me-1"></i> สร้าง Sales Order</>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateOrder;
